import { Controller } from '@hotwired/stimulus'
import { toCzech } from '../datepicker_controller'

// Nová zakázka (ux-redesign §4.4) and the booking rooms modal (ORD-202): the preview frame reloads as a GET with the form's order and booking fields, debounced 300 ms.
// The server answers with the operation's plan (Orders::CreateWithBooking, PartnerBookings::SetRooms); nothing is computed or written here.
// I-UX-S5-02: the end the plan proposes (start + route nights) is copied into an empty Konec. The hidden ends_on_source
// says whether Konec still holds that copy ("proposed": the server ignores it and proposes again, on preview and on save)
// or the user's end ("typed"). It is server-rendered, so a 422 re-render keeps it; a submit cancels the pending preview.
export default class PreviewController extends Controller<HTMLElement> {
  static override targets = ['form', 'frame', 'endsHint', 'endsSource']
  static override values = { url: String, pristine: Boolean }
  declare readonly formTarget: HTMLFormElement
  declare readonly frameTarget: HTMLElement
  declare readonly endsHintTarget: HTMLElement
  declare readonly hasEndsHintTarget: boolean
  declare readonly endsSourceTarget: HTMLInputElement
  declare readonly hasEndsSourceTarget: boolean
  declare readonly urlValue: string
  declare readonly pristineValue: boolean
  private timer: number | undefined
  // I-CALM-03 / I-WALK-02: a fresh Nová zakázka (pristine, set by the server) asks with pristine=1 and the fields the
  // user has left (blur) or changed (touched[]); the server lists only their errors, so „datum“ doesn't complain while
  // the user is still typing the name. A 422 re-render or a prefilled form is not pristine: every error shows.
  private readonly touched = new Set<string>()
  private proposedEnd = ''

  override connect(): void {
    if (this.hasEndsSourceTarget && this.endsSourceTarget.value === 'proposed') this.proposedEnd = this.endsInput?.value ?? ''
    this.frameTarget.addEventListener('turbo:frame-load', this.onLoad)
    this.formTarget.addEventListener('submit', this.onSubmit)
    this.formTarget.addEventListener('focusout', this.onLeave)
    this.formTarget.addEventListener('change', this.onLeave)
    this.load()
  }

  override disconnect(): void {
    window.clearTimeout(this.timer)
    this.frameTarget.removeEventListener('turbo:frame-load', this.onLoad)
    this.formTarget.removeEventListener('submit', this.onSubmit)
    this.formTarget.removeEventListener('focusout', this.onLeave)
    this.formTarget.removeEventListener('change', this.onLeave)
  }

  refresh(): void {
    this.syncSource()
    window.clearTimeout(this.timer)
    this.timer = window.setTimeout(() => this.load(), 300)
  }

  private get endsInput(): HTMLInputElement | null {
    return this.formTarget.querySelector<HTMLInputElement>('input[name="record[ends_on]"]')
  }

  // "proposed" while Konec still equals the copied proposal; any other value (typed, cleared) is the user's.
  private syncSource(): void {
    if (!this.hasEndsSourceTarget) return
    const proposed = this.proposedEnd !== '' && this.endsInput?.value === this.proposedEnd
    this.endsSourceTarget.value = proposed ? 'proposed' : 'typed'
  }

  // Focus moving inside the same field (the datepicker's calendar, a searchable select's box) is not leaving it.
  private readonly onLeave = (event: Event): void => {
    const field = event.target
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement) || field.name === '') return
    const next = event instanceof FocusEvent ? event.relatedTarget : null
    const wrapper = field.closest('[data-quick-field]')
    if (event.type === 'focusout' && next instanceof Node && wrapper?.contains(next)) return
    if (this.touched.has(field.name)) return
    this.touched.add(field.name)
    this.refresh()
  }

  private readonly onSubmit = (): void => {
    window.clearTimeout(this.timer) // no stale preview after the submit; the server recomputes a "proposed" end
    this.syncSource()
  }

  private load(): void {
    this.syncSource()
    const query = new URLSearchParams()
    for (const [name, value] of new FormData(this.formTarget)) {
      if (typeof value !== 'string') continue
      if (name.startsWith('record[') || name.startsWith('booking[') || name === 'create_booking' || name === 'ends_on_source') query.append(name, value)
    }
    if (this.pristineValue) {
      query.append('pristine', '1')
      for (const name of this.touched) query.append('touched[]', name)
    }
    this.frameTarget.setAttribute('src', `${this.urlValue}?${query.toString()}`)
  }

  private readonly onLoad = (): void => {
    const input = this.endsInput
    if (!input) return
    const iso = this.frameTarget.querySelector<HTMLElement>('[data-proposed-ends-on]')?.dataset['proposedEndsOn']
    const auto = input.value === '' || input.value === this.proposedEnd
    if (iso && auto) {
      input.value = toCzech(iso)
      this.proposedEnd = input.value
    } else if (!iso && auto && this.proposedEnd !== '') {
      input.value = '' // the proposal is gone (route without length, no start): no stale copy
      this.proposedEnd = ''
    }
    this.syncSource()
    if (this.hasEndsHintTarget) this.endsHintTarget.hidden = this.proposedEnd === '' || input.value !== this.proposedEnd
  }
}
